import { forgotPasswordSchema, resetPasswordSchema, verifyEmailSchema } from '@alicerce/contracts';
import { Alert, Button, PageLoader, PasswordField, TextField } from '@alicerce/ui';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { errorMessage } from '@/lib/api';
import { useZodForm } from '@/lib/form';
import { useApi } from '@/lib/session';
import { AuthLayout, withPasswordConfirmation } from './layout';

const backToLogin = (
  <Link className="font-medium text-brand-700 hover:underline" to="/entrar">
    Voltar para o login
  </Link>
);

export function ForgotPasswordPage() {
  const api = useApi();
  const [sent, setSent] = useState<string | null>(null);
  const form = useZodForm(forgotPasswordSchema, { email: '' });
  const onSubmit = form.submit(async (data) => {
    const r = await api.post<{ message: string }>('/v1/auth/forgot-password', data, { noTenant: true });
    setSent(r.message);
  });
  return (
    <AuthLayout title="Esqueci minha senha" subtitle="Enviamos um link para você criar uma senha nova." footer={backToLogin}>
      {sent ? (
        <Alert tone="success">{sent}</Alert>
      ) : (
        <form className="space-y-4" onSubmit={onSubmit} noValidate>
          {form.formError && <Alert tone="danger">{form.formError}</Alert>}
          <TextField label="E-mail" type="email" autoComplete="email" value={form.values.email} error={form.errors.email} onChange={(e) => form.set('email', e.target.value)} />
          <Button type="submit" className="w-full" loading={form.submitting}>
            Enviar link
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

const resetPasswordFormSchema = withPasswordConfirmation(resetPasswordSchema);

export function ResetPasswordPage() {
  const api = useApi();
  const [params] = useSearchParams();
  const [done, setDone] = useState(false);
  const form = useZodForm(resetPasswordFormSchema, { token: params.get('token') ?? '', password: '', passwordConfirmation: '' });
  const onSubmit = form.submit(async ({ passwordConfirmation: _confirm, ...data }) => {
    await api.post('/v1/auth/reset-password', data, { noTenant: true });
    setDone(true);
  });
  return (
    <AuthLayout title="Criar senha nova" footer={backToLogin}>
      {done ? (
        <Alert tone="success">Senha alterada. Entre com a senha nova.</Alert>
      ) : (
        <form className="space-y-4" onSubmit={onSubmit} noValidate>
          {(form.formError || form.errors.token) && <Alert tone="danger">{form.formError ?? 'Link inválido ou expirado. Peça um novo.'}</Alert>}
          <PasswordField
            label="Senha nova"
            autoComplete="new-password"
            hint="Pelo menos 8 caracteres."
            value={form.values.password}
            error={form.errors.password}
            onChange={(e) => form.set('password', e.target.value)}
          />
          <PasswordField
            label="Confirmar senha nova"
            autoComplete="new-password"
            value={form.values.passwordConfirmation}
            error={form.errors.passwordConfirmation}
            onChange={(e) => form.set('passwordConfirmation', e.target.value)}
          />
          <Button type="submit" className="w-full" loading={form.submitting}>
            Salvar senha
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

export function VerifyEmailPage() {
  const api = useApi();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const verify = useMutation({ mutationFn: () => api.post('/v1/auth/verify-email', verifyEmailSchema.parse({ token }), { noTenant: true }) });
  const { mutate } = verify;
  useEffect(() => {
    if (token.length >= 10) mutate();
  }, [token, mutate]);
  return (
    <AuthLayout title="Confirmar e-mail" footer={<Link className="font-medium text-brand-700 hover:underline" to="/">Ir para o painel</Link>}>
      {token.length < 10 ? (
        <Alert tone="danger">Link inválido ou expirado.</Alert>
      ) : verify.isPending || verify.isIdle ? (
        <PageLoader />
      ) : verify.isError ? (
        <Alert tone="danger">{errorMessage(verify.error)}</Alert>
      ) : (
        <Alert tone="success">E-mail confirmado. Obrigado!</Alert>
      )}
    </AuthLayout>
  );
}
